import styled from '@emotion/styled'
import { createContext, FunctionComponent, ReactNode, RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Flyout, FlyoutProps } from '~/components/Flyout'
import { Modal } from '~/components/Modal'
import { WithClassName } from '~/types/utils'

// Context for components to register close guards
type CloseGuardContextType = {
  guardRef: RefObject<(() => Promise<boolean> | boolean) | null>
}

export const CloseGuardContext = createContext<CloseGuardContextType | null>(null)

type AdditionalProps = object
type OnCloseCallbacks = {
  onBeforeDefaultClose?: () => void | Promise<boolean>
  onAfterDefaultClose?: () => void
}
type ContainerProps = Pick<FlyoutProps, 'color' | 'width' | 'closeTrackingLabel'> & OnCloseCallbacks
type GlobalFlyoutHandler = {
  isOpen: boolean
  open: <T extends AdditionalProps>(comp: (props: T) => Promise<ReactNode> | ReactNode, containerProps: ContainerProps, additionalProps?: T) => void
  close: () => Promise<void>
}
const GlobalFlyoutContext = createContext<GlobalFlyoutHandler>({
  isOpen: false,
  open: () => {},
  close: async () => {},
})

export const useGlobalFlyout = () => useContext(GlobalFlyoutContext)

type Content<T extends AdditionalProps = AdditionalProps> = {
  Component: FunctionComponent<T>
  additionalProps?: T
  containerProps: ContainerProps
}

const defaultContent: Content = {
  Component: () => null,
  additionalProps: {},
  containerProps: { closeTrackingLabel: 'common-flyout-close' },
}

const useFlyout = () => {
  const [isOpen, setOpen] = useState(false)
  const [content, setContent] = useState<Content>(defaultContent)
  const handler = useMemo((): GlobalFlyoutHandler => {
    return {
      isOpen,
      open: (comp, containerProps, additionalProps) => {
        setContent({
          Component: comp as unknown as FunctionComponent<AdditionalProps>, // couldn't type it better
          additionalProps,
          containerProps,
        })
        setOpen(true)
      },
      close: async () => {
        setOpen(false)
        return new Promise(
          (resolve) =>
            setTimeout(() => {
              setContent(defaultContent)
              resolve()
            }, Modal.CLOSE_TIMEOUT_MS + 1) // modal close timeout +1, could cause issue if we close then reopen, need some tests
        )
      },
    }
  }, [isOpen])

  return {
    content,
    handler,
  }
}

type GlobalFlyoutWrapperProps = WithClassName & {
  children: ReactNode
}
const BaseGlobalFlyoutWrapper: FunctionComponent<GlobalFlyoutWrapperProps> = ({ children }) => {
  const {
    content: { Component, additionalProps, containerProps },
    handler,
  } = useFlyout()

  const closeGuardRef = useRef<(() => Promise<boolean> | boolean) | null>(null)
  const closeGuardContext = useMemo(() => ({ guardRef: closeGuardRef }), [])

  /**
   * A flyout belongs to the page it was opened over, so it leaves with it. Without this it survived the
   * navigation and hung over whatever came next — a media's panel on top of the ad units list.
   *
   * The path only: a flyout that writes its state into the query string (a selected row, a filter) must
   * not close itself every time it does. The close guard is deliberately skipped — it exists to ask
   * about unsaved changes on a page still there to go back to, and this one is already gone.
   */
  const { pathname } = useLocation()
  const openedAt = useRef(pathname)
  useEffect(() => {
    if (openedAt.current === pathname) return
    openedAt.current = pathname
    if (handler.isOpen) void handler.close()
  }, [pathname, handler])

  return (
    <GlobalFlyoutContext.Provider value={handler}>
      <Flyout.Container
        {...containerProps}
        isOpen={handler.isOpen}
        onRequestClose={async () => {
          const { onBeforeDefaultClose, onAfterDefaultClose } = containerProps || {}

          // Check component-level guard first
          if (closeGuardRef.current) {
            const canClose = await closeGuardRef.current()
            if (!canClose) {
              return // Prevent close
            }
          }

          // Then check legacy onBeforeDefaultClose callback
          const shouldClose = onBeforeDefaultClose ? await onBeforeDefaultClose() : true
          if (shouldClose !== false) {
            await handler.close()
            onAfterDefaultClose?.()
          }
        }}
      >
        <CloseGuardContext.Provider value={closeGuardContext}>
          <Component {...additionalProps} />
        </CloseGuardContext.Provider>
      </Flyout.Container>
      {children}
    </GlobalFlyoutContext.Provider>
  )
}
export const GlobalFlyoutWrapper = styled(BaseGlobalFlyoutWrapper)``
